import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { logger } from '../../../utils/logger';

// Dynamically discover all SVGs under assets/icons
const iconContext = require.context('../../../assets/icons', true, /\.svg$/);
const iconKeys = iconContext.keys();

function resolveIconKey(name) {
  // Allow names like "arrows/arrow_back" or "arrow_back"
  const normalized = name.replace(/^\/+/, '');
  let match = iconKeys.find(k => k.endsWith(`/${normalized}.svg`));
  if (!match) {
    // Try each subfolder with the basename
    const base = normalized.split('/').pop();
    match = iconKeys.find(k => k.endsWith(`/${base}.svg`));
  }
  return match || null;
}

function toCurrentColor(svgText) {
  // Replace any explicit fills with currentColor to allow runtime color control
  // 1) fill="#xxxxxx" or fill="rgb(...)" or fill="none" (keep "none")
  return svgText
    .replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
    .replace(/style="([^"]*)"/g, (m, styles) => {
      // Remove inline fill from style attribute if present
      const cleaned = styles
        .split(';')
        .map(s => s.trim())
        .filter(s => s && !/^fill\s*:/.test(s))
        .join('; ');
      return cleaned ? `style="${cleaned}"` : '';
    });
}

function injectSize(svgText, width, height) {
  // Remove existing width/height on <svg>, re-inject ours
  let updated = svgText
    .replace(/\swidth="[^"]*"/, '')
    .replace(/\sheight="[^"]*"/, '');

  // Insert width/height into opening <svg ...>
  updated = updated.replace(
    /<svg\b([^>]*)>/,
    `<svg$1 width="${width}" height="${height}">`
  );
  return updated;
}

const Icon = ({
  name,
  theme = 'light',
  color,            // e.g., "var(--bg--quaternary-dark)" or "#FF00AA"
  width = 24,
  height = 24,
  className = '',
  title,
  ...rest
}) => {
  const [svgMarkup, setSvgMarkup] = useState(null);

  const resolvedColor = useMemo(() => {
    if (color) return color;
    return undefined; // Do not set a default color if not provided
  }, [color]);

  // Resolve the icon URL from require.context
  const iconKey = useMemo(() => resolveIconKey(name), [name]);
  const iconUrl = useMemo(() => {
    if (!iconKey) return null;
    const mod = iconContext(iconKey);
    // CRA usually puts the URL on default
    return typeof mod === 'string' ? mod : mod?.default;
  }, [iconKey]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      if (!iconUrl) {
        setSvgMarkup(null);
        return;
      }
      try {
        const res = await fetch(iconUrl);
        const text = await res.text();
        // Only apply toCurrentColor if color is provided to avoid overriding original fills
        const processedText = resolvedColor ? toCurrentColor(text) : text;
        const sized = injectSize(processedText, width, height);

        // Optional <title> injection for accessibility
        const withTitle = title
          ? sized.replace(
              /<svg\b([^>]*)>/,
              `<svg$1><title>${title}</title>`
            )
          : sized;

        if (isMounted) setSvgMarkup(withTitle);
      } catch (e) {
        logger.warn(`Failed to load SVG: ${name}`, e);
        if (isMounted) setSvgMarkup(null);
      }
    }

    load();
    return () => { isMounted = false; };
  }, [iconUrl, name, width, height, title]);

  if (!iconKey || !iconUrl) {
    logger.warn(`Icon not found: "${name}"`);
    return null;
  }

  // Apply color via currentColor
  const style = useMemo(() => {
    const baseStyle = { lineHeight: 0 };
    if (resolvedColor) baseStyle.color = resolvedColor;
    return baseStyle;
  }, [resolvedColor]);

  return (
    <span
      className={`icon icon-${name.replace(/[\\/]/g, '-')}${className ? ' ' + className : ''}`}
      style={style}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={svgMarkup ? { __html: svgMarkup } : undefined}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : 'presentation'}
      {...rest}
    />
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired, // e.g. "arrows/arrow_back" or "arrow_back"
  theme: PropTypes.oneOf(['light', 'dark']),
  color: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  title: PropTypes.string
};

export default Icon;
export { Icon };